import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
} from 'react'
import { useAtomValue } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'
import { useGlobalState } from '@highlight/ipc/react'
import { GlobalState } from '@highlight/ipc'
import { Button, Combobox } from '@highlight/ui'
import { Mic, Monitor, Search } from 'lucide-react'
import type { TranscriptEntry } from '@highlight/common'
import { activeTranscriptEntriesAtom } from '@renderer/hooks/audio/use-audio-transcript'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useAudioNoteRenameContext } from './types'
import { useUser } from '@renderer/hooks/useUser'
import { getUserFullName } from '@renderer/utils/user-name'
import { usePeople, usePeopleByEmail, type Person } from '@renderer/chat/hooks/usePeople'
import type { AudioNoteState } from '@shared/types/AudioNoteState'
import { useMeetingParticipants } from '@renderer/hooks/audio/use-meeting-participants'
import { useSpeakerResolverEvidence } from '@renderer/hooks/audio/use-speaker-resolver-evidence'
import { isSingleMicSpeaker, type UserVoiceMatches } from '@shared/utils/audio-notes/mic-label'
import { USER_VOICE_MATCHES_STATE } from '@shared/self-voice'
import { resolveEntrySpeaker } from '@renderer/utils/audio-note-helpers'
import { AUDIO_SOURCE_LABELS } from './audio-source'

const personDisplayName = (person: Person, fallbackEmail: string): string => {
  const display = person.displayName?.trim()
  if (display) return display
  const composed = [person.givenName, person.familyName].filter((part) => part?.trim()).join(' ')
  return composed || person.primaryEmail || fallbackEmail
}

// Backend-provided ranking signal (calendar/Linear/context co-occurrence). The
// generated Person type may lag the API until `gen:api-types` is re-run, so read
// it defensively — it's 0 until the backend that exposes it ships.
const personInteractionScore = (person: Person): number =>
  (person as { interactionScore?: number }).interactionScore ?? 0

export type SpeakerRenameTarget = {
  speakerName: string
  /** When provided, scopes the rename to these specific DG indices instead of all indices matching the display name */
  dgSpeakerIndices?: Array<string>
}

type OpenSpeakerRename = (target: SpeakerRenameTarget, anchor: HTMLElement) => void

/**
 * Only the open callback travels through context, and it is stable for the
 * provider's lifetime — so opening the rename UI never re-renders the rows.
 */
const SpeakerRenameContext = createContext<OpenSpeakerRename | null>(null)

/**
 * Hosts a single rename combobox for a whole transcript.
 *
 * Previously every header row mounted its own `Menu` — a Base UI root, a styled
 * `Menu.Trigger` button running `buttonVariants()` through cva on each render,
 * and a portal — which in the standalone meeting-note window (deliberately
 * unvirtualized) meant one per speaker turn across the entire note. One instance
 * anchored per click costs the same whether the transcript has ten rows or
 * fifteen hundred.
 */
export function SpeakerRenameProvider({ children }: PropsWithChildren) {
  const [target, setTarget] = useState<SpeakerRenameTarget | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)

  const openRename = useCallback<OpenSpeakerRename>((next, anchor) => {
    anchorRef.current = anchor
    setTarget(next)
  }, [])

  const handleClose = useCallback(() => {
    setTarget(null)
    // No Base UI trigger to hand focus back to, so return it to the row the user
    // actually came from rather than dropping it on the body.
    anchorRef.current?.focus()
  }, [])

  return (
    <SpeakerRenameContext.Provider value={openRename}>
      {children}
      {target ? <SpeakerRenameCombobox target={target} anchorRef={anchorRef} onClose={handleClose} /> : null}
    </SpeakerRenameContext.Provider>
  )
}

/**
 * Frozen at module scope. This is the class set the old `Menu.Trigger` produced
 * from `variant="tertiary" size="tiny"` plus the dotted underline its wrapped
 * span carried — constant on every row, so there is no reason to run it through
 * cva and twMerge per render.
 */
const SPEAKER_TRIGGER_CLASS = [
  // `inline`, not a flex button: this sits in the middle of the header line
  // alongside the mic icon and the timestamp, so it has to stay in the text flow
  // and inherit the row's size and colour exactly as the old trigger did.
  // -mx-1 keeps the name flush with the transcript text; the padding only widens the hover tint.
  'user-select-none inline cursor-pointer whitespace-nowrap rounded-lg corner-smooth -mx-1 px-1 py-0 align-baseline text-inherit',
  // `bg-transparent` is load-bearing in light mode: the base layer only zeroes the
  // UA button background under `.dark`, so without it every row paints the native
  // grey `buttonface` pill and the hover tint has nothing left to reveal.
  'bg-transparent hover:bg-element-hover transition-[background-color,color,box-shadow]',
].join(' ')

/**
 * The per-row affordance: a real button (keyboard-reachable, focusable) that
 * carries no subscriptions and no popup machinery. Without a provider it renders
 * the name as plain text rather than a control that does nothing.
 */
export const SpeakerRenameTrigger = memo(function SpeakerRenameTrigger({
  speakerName,
  dgSpeakerIndices,
  children,
}: {
  speakerName: string
  dgSpeakerIndices?: Array<string>
  children: ReactNode
}) {
  const openRename = useContext(SpeakerRenameContext)

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openRename?.({ speakerName, dgSpeakerIndices }, event.currentTarget)
    },
    [openRename, speakerName, dgSpeakerIndices],
  )

  if (!openRename) {
    return <>{children}</>
  }

  return (
    <button type="button" aria-haspopup="listbox" onClick={handleClick} className={SPEAKER_TRIGGER_CLASS}>
      {children}
    </button>
  )
})

const SpeakerRenameCombobox = memo(function SpeakerRenameCombobox({
  target,
  anchorRef,
  onClose,
}: {
  target: SpeakerRenameTarget
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
}) {
  const { speakerName, dgSpeakerIndices: propDgSpeakerIndices } = target
  // Mounts on open, so the current name is the initial query — this replaces the
  // effect that used to re-seed it whenever `open` flipped.
  const [query, setQuery] = useState(speakerName)

  const renameCtx = useAudioNoteRenameContext()
  const queryClient = useQueryClient()
  const user = useUser()
  const ownerName = getUserFullName(user)
  const selfLabel = ownerName ?? 'Me'
  const entries = useAtomValue(activeTranscriptEntriesAtom)
  const effectiveEntries = entries.length > 0 ? entries : (renameCtx?.transcriptEntries ?? [])
  const [resolvedNames] = useGlobalState<Record<string, string>>('speaker-resolved-names', {})
  const participants = useMeetingParticipants()
  const [audioNoteState] = useGlobalState<AudioNoteState | undefined>('audio-note-state', undefined)
  const [currentOverrides] = useGlobalState<Record<string, string>>('speaker-manual-overrides', {})
  const [userVoices] = useGlobalState<UserVoiceMatches>(USER_VOICE_MATCHES_STATE, {})
  const evidenceState = useSpeakerResolverEvidence()

  /** Persist rename on a saved note without round-tripping entries through GlobalState. */
  const persistSavedNoteRename = useCallback(
    async (noteId: string, mapEntries: (entries: Array<TranscriptEntry>) => Array<TranscriptEntry>): Promise<void> => {
      const full = await AudioNoteIpcRenderer.getAudioNote(noteId)
      if (!full?.transcriptEntries?.length) {
        console.warn('[SpeakerRename] No transcript entries on note', noteId)
        return
      }
      const updatedEntries = mapEntries(full.transcriptEntries)
      await AudioNoteIpcRenderer.updateAudioNote({ id: noteId, updates: { transcriptEntries: updatedEntries } })
      await queryClient.invalidateQueries({ queryKey: ['audio-note', noteId] })
      console.log('[SpeakerRename] Backend update succeeded')
    },
    [queryClient],
  )

  // Find DG indices for this speaker. When propDgSpeakerIndices is provided (from the
  // message that was clicked), use those directly to scope the rename to the specific
  // speaker instance. Otherwise fall back to scanning all entries by display name.
  // Overrides win — the resolver merges them in last, and saved notes use only these.
  const displayNames = useMemo(() => ({ ...resolvedNames, ...currentOverrides }), [resolvedNames, currentOverrides])

  const matchingDgIndices = useMemo(() => {
    if (propDgSpeakerIndices && propDgSpeakerIndices.length > 0) {
      return propDgSpeakerIndices
    }
    const indices = new Set<string>()
    const ctx = {
      resolvedNames: displayNames,
      ownerName,
      singleMicSpeaker: isSingleMicSpeaker(effectiveEntries),
      userVoices,
    }
    for (const entry of effectiveEntries) {
      if (!entry.dgSpeakerIndex) continue
      if (resolveEntrySpeaker(entry, ctx) === speakerName) {
        indices.add(entry.dgSpeakerIndex)
      }
    }
    return Array.from(indices)
  }, [propDgSpeakerIndices, effectiveEntries, displayNames, speakerName, ownerName, userVoices])

  const isManuallyOverridden = useMemo(() => {
    return matchingDgIndices.some((idx) => currentOverrides[idx] !== undefined)
  }, [matchingDgIndices, currentOverrides])

  // Compute evidence info for this speaker's DG indices
  const speakerEvidence = useMemo(() => {
    if (matchingDgIndices.length === 0) return null

    // Strip prefixes (e.g. "sys:0" → "0") to match bare indices in evidence state
    const bareIndices = matchingDgIndices.map((idx) => {
      const colonPos = idx.indexOf(':')
      return colonPos >= 0 ? idx.slice(colonPos + 1) : idx
    })

    const entries = evidenceState.speakers.filter((s) => bareIndices.includes(s.dgIndex))
    if (entries.length === 0) return null

    const totalVotes = entries.reduce((sum, e) => sum + e.totalVotes, 0)
    const prefix = matchingDgIndices[0]?.startsWith('mic:') ? 'mic' : 'sys'

    // Aggregate votes across all matching DG indices for sorting suggestions
    const votes: Record<string, number> = {}
    for (const entry of entries) {
      for (const [name, count] of Object.entries(entry.votes)) {
        votes[name] = (votes[name] ?? 0) + count
      }
    }

    return { totalVotes, prefix, votes }
  }, [matchingDgIndices, evidenceState.speakers])

  // Gather participant names (excluding self)
  const participantNames = useMemo(() => {
    const names = new Set<string>()
    for (const p of Object.values(participants)) {
      if (p.name && !p.isSelf) {
        names.add(p.name)
      }
    }
    return names
  }, [participants])

  const peopleByEmail = usePeopleByEmail()
  const { data: allPeople } = usePeople()

  // Gather calendar attendee emails as fallback suggestions
  const calendarEmails = useMemo(() => {
    const emails = new Set<string>()
    const meeting = audioNoteState?.meeting
    if (!meeting) return emails

    if ('calendarEvent' in meeting && meeting.calendarEvent?.attendees) {
      for (const a of meeting.calendarEvent.attendees) {
        if (a.email) emails.add(a.email)
      }
    }
    if ('attendees' in meeting && Array.isArray(meeting.attendees)) {
      for (const a of meeting.attendees) {
        if (typeof a === 'string') emails.add(a)
      }
    }
    return emails
  }, [audioNoteState?.meeting])

  // Resolve attendee emails to Person display names where possible, deduping by Person.id so
  // alias emails collapse into a single suggestion. Unresolved emails fall through unchanged.
  const calendarSuggestions = useMemo(() => {
    const seenPersonIds = new Set<string>()
    const labels: Array<string> = []
    for (const email of calendarEmails) {
      const person = peopleByEmail.get(email.trim().toLowerCase())
      if (person) {
        if (seenPersonIds.has(person.id)) continue
        seenPersonIds.add(person.id)
        labels.push(personDisplayName(person, email))
      } else {
        labels.push(email)
      }
    }
    return labels
  }, [calendarEmails, peopleByEmail])

  // Same-org people: any Person whose primary/additional email shares the user's email domain.
  // This surfaces colleagues for filtering in the dropdown while excluding integration-bot
  // Person records (linear/notion/cursor/etc.) that live on third-party domains.
  const sameOrgPeople = useMemo(() => {
    if (!allPeople || allPeople.length === 0) return []
    const selfEmail = user?.email?.trim().toLowerCase()
    const userDomain = selfEmail?.split('@')[1]
    if (!userDomain) return []
    const domainSuffix = `@${userDomain}`
    const matched: Array<Person> = []
    for (const person of allPeople) {
      const candidateEmails = [person.primaryEmail, ...(person.additionalEmails ?? [])]
        .filter((e): e is string => !!e)
        .map((e) => e.toLowerCase())
      if (!candidateEmails.some((e) => e.endsWith(domainSuffix))) continue
      if (candidateEmails.includes(selfEmail!)) continue
      matched.push(person)
    }
    // Rank colleagues by importance then interaction score (most-interacted
    // first) so the most-likely person surfaces at the top, rather than the
    // arbitrary creation order the /people list returns.
    matched.sort(
      (a, b) => Number(b.important) - Number(a.important) || personInteractionScore(b) - personInteractionScore(a),
    )
    return matched.map((person) => personDisplayName(person, person.primaryEmail ?? ''))
  }, [allPeople, user?.email])

  // Build suggestion list
  const allSuggestions = useMemo(() => {
    const seen = new Set<string>()
    const result: Array<string> = []
    const pushCandidate = (name: string) => {
      const trimmed = name.trim()
      if (!trimmed || trimmed === speakerName) return
      const key = trimmed.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      result.push(trimmed)
    }

    if (speakerName !== selfLabel) pushCandidate(selfLabel)
    for (const name of participantNames) pushCandidate(name)
    for (const label of calendarSuggestions) pushCandidate(label)
    for (const label of sameOrgPeople) pushCandidate(label)

    return result
  }, [participantNames, calendarSuggestions, sameOrgPeople, speakerName, selfLabel])

  // Evidence-ranked suggestions. The query filtering that used to live here is
  // gone: Combobox.Root filters `items` against its own input, which also brings
  // arrow-key navigation and listbox/option semantics the hand-rolled list never
  // had.
  const rankedSuggestions = useMemo(() => {
    const votes = speakerEvidence?.votes
    if (!votes) return allSuggestions
    return [...allSuggestions].sort((a, b) => (votes[b] ?? 0) - (votes[a] ?? 0))
  }, [allSuggestions, speakerEvidence])

  const trimmedQuery = query.trim()
  // The prefill is a name, not a search term — filtering against it hid every participant.
  const isUntouchedPrefill = query === speakerName
  const isNewName =
    trimmedQuery.length > 0 &&
    trimmedQuery !== speakerName &&
    !rankedSuggestions.some((s) => s.toLowerCase() === trimmedQuery.toLowerCase())

  // The free-text name rides in as a real item, so Enter and the arrow keys reach
  // it like any other option instead of it sitting outside the list as a button.
  //
  // It goes FIRST, which is both where the old "Use …" row rendered and what makes
  // `autoHighlight` reproduce the old Enter behaviour: the previous input handled
  // Enter itself and always submitted the raw query, so the typed name — not the
  // top suggestion — has to be the highlighted default.
  const items = useMemo(
    () => (isNewName ? [trimmedQuery, ...rankedSuggestions] : rankedSuggestions),
    [rankedSuggestions, isNewName, trimmedQuery],
  )

  const handleRename = useCallback(
    (newName: string) => {
      const trimmedName = newName.trim()
      if (!trimmedName) return

      const savedNoteId = audioNoteState?.savedNote?.id
      console.log('[SpeakerRename] Renaming', speakerName, '→', trimmedName, {
        dgIndices: matchingDgIndices,
        hasSavedNote: !!savedNoteId,
        hasRenameCtx: !!renameCtx,
        entryCount: renameCtx?.transcriptEntries.length ?? effectiveEntries.length,
      })

      if (matchingDgIndices.length > 0) {
        // Live session or entries with dgSpeakerIndex — use override-based flow
        console.log('[SpeakerRename] Using dgSpeakerIndex-based override flow')
        const updated = { ...currentOverrides }
        for (const idx of matchingDgIndices) {
          updated[idx] = trimmedName
        }
        GlobalState.set('speaker-manual-overrides', updated)

        // Forward to the audio session DO so server-side consumers (staged
        // actions, deep research, the eventual saved transcript) reflect the
        // rename. Best-effort — main-side IPC swallows errors. Only fires
        // during a live session; no-op once the session has been closed.
        const activeAudioSessionId = audioNoteState?.audioSessionId
        if (activeAudioSessionId) {
          for (const idx of matchingDgIndices) {
            AudioNoteIpcRenderer.applyAudioSessionSpeakerRename({
              audioSessionId: activeAudioSessionId,
              dgSpeakerIndex: idx,
              name: trimmedName,
            }).catch((err) => console.error('[SpeakerRename] applyAudioSessionSpeakerRename failed:', err))
          }
        }

        // For saved notes: persist into store/by-id — never rewrite GlobalState
        // with a full transcriptEntries array (stripped from the wire).
        if (savedNoteId) {
          const matchSet = new Set(matchingDgIndices)
          console.log('[SpeakerRename] Persisting dgIndex-based rename to backend, noteId:', savedNoteId)
          void persistSavedNoteRename(savedNoteId, (entries) =>
            entries.map((entry) =>
              entry.dgSpeakerIndex && matchSet.has(entry.dgSpeakerIndex) ? { ...entry, speaker: trimmedName } : entry,
            ),
          ).catch((err) => console.error('[SpeakerRename] Failed to persist rename:', err))
        }
      } else if (renameCtx) {
        // Saved note via context (opened from list) — match by speaker name
        // Match the display name — a mic entry persists as "My mic - A" but shows as "{Owner}'s mic - A".
        const ctx = {
          resolvedNames: displayNames,
          ownerName,
          singleMicSpeaker: isSingleMicSpeaker(renameCtx.transcriptEntries),
        }
        const updatedEntries = renameCtx.transcriptEntries.map((entry) =>
          resolveEntrySpeaker(entry, ctx) === speakerName ? { ...entry, speaker: trimmedName } : entry,
        )
        const matchCount = updatedEntries.reduce(
          (count, entry, i) => count + (entry === renameCtx.transcriptEntries[i] ? 0 : 1),
          0,
        )
        console.log(
          '[SpeakerRename] Using context name-based flow, matched',
          matchCount,
          'entries, noteId:',
          renameCtx.audioNoteId,
        )
        renameCtx.onEntriesChanged(updatedEntries)
      } else if (savedNoteId) {
        // Just-recorded note: entries live in main/store, not on the GlobalState wire
        console.log('[SpeakerRename] Persisting name-based rename for saved note', savedNoteId)
        void persistSavedNoteRename(savedNoteId, (entries) => {
          const ctx = {
            resolvedNames: displayNames,
            ownerName,
            singleMicSpeaker: isSingleMicSpeaker(entries),
          }
          return entries.map((entry) =>
            resolveEntrySpeaker(entry, ctx) === speakerName ? { ...entry, speaker: trimmedName } : entry,
          )
        }).catch((err) => console.error('[SpeakerRename] Failed to persist rename:', err))
      } else {
        console.log('[SpeakerRename] No dgIndices, no context, no saved note — rename is a no-op')
      }

      onClose()
    },
    [
      currentOverrides,
      matchingDgIndices,
      audioNoteState,
      speakerName,
      renameCtx,
      onClose,
      persistSavedNoteRename,
      effectiveEntries.length,
      displayNames,
      ownerName,
    ],
  )

  const handleReset = useCallback(() => {
    const updated = { ...currentOverrides }
    for (const idx of matchingDgIndices) {
      delete updated[idx]
    }
    GlobalState.set('speaker-manual-overrides', updated)

    // Mirror the clear server-side so AI prompts stop applying the override.
    const activeAudioSessionId = audioNoteState?.audioSessionId
    if (activeAudioSessionId) {
      for (const idx of matchingDgIndices) {
        AudioNoteIpcRenderer.applyAudioSessionSpeakerRename({
          audioSessionId: activeAudioSessionId,
          dgSpeakerIndex: idx,
          name: null,
        }).catch((err) => console.error('[SpeakerRename] applyAudioSessionSpeakerRename (reset) failed:', err))
      }
    }

    // For saved notes: revert transcript entries to their original speaker names
    // by removing the override — the entry.speaker stays as the previously-persisted
    // value, so the next display pass will use entry.speaker directly
    onClose()
  }, [currentOverrides, matchingDgIndices, audioNoteState?.audioSessionId, onClose])

  // Base UI won't auto-focus an input inside the popup unless it is the anchor,
  // so the field is the popup's `initialFocus`. That focus lands a frame after
  // mount, so the name is selected on focus. Only the first focus selects, so
  // clicking back in still places the caret.
  const inputRef = useRef<HTMLInputElement>(null)
  const selectedOnOpenRef = useRef(false)
  const selectNameOnOpen = useCallback((event: FocusEvent<HTMLInputElement>) => {
    if (selectedOnOpenRef.current) {
      return
    }
    selectedOnOpenRef.current = true
    event.currentTarget.select()
  }, [])

  return (
    <Combobox
      // `contents` so the root's layout box does not land in the transcript's
      // flex column and open a stray gap — everything real is portaled.
      className="contents"
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
      // Without this, Enter selects nothing until the user arrows into the list.
      // Held until the prefill is touched so Enter on open can't apply a suggestion.
      autoHighlight={!isUntouchedPrefill}
      filter={isUntouchedPrefill ? null : undefined}
      items={items}
      inputValue={query}
      onInputValueChange={setQuery}
      onValueChange={(value) => {
        if (typeof value === 'string' && value) handleRename(value)
      }}
    >
      {/* Portal/Positioner/Popup composed explicitly rather than via
          `Combobox.Content`: that convenience wrapper forwards only `anchor` and
          `sideOffset`, and this needs `align="start"` to sit under the name the
          way the old menu did. */}
      <Combobox.Portal>
        <Combobox.Positioner anchor={anchorRef as RefObject<HTMLElement>} side="bottom" align="start" sideOffset={6}>
          {/* Overrides the popup's default `w-[var(--anchor-width)]` — the anchor
              is a speaker name, so anchor-width would be a few characters wide. */}
          <Combobox.Popup className="w-72 p-0" initialFocus={inputRef}>
            <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
              <span className="text-foreground-secondary type-ui-sm">Rename speaker</span>
              {isManuallyOverridden && (
                <Button variant="ghost" onClick={handleReset}>
                  Reset
                </Button>
              )}
            </div>

            {speakerEvidence && (
              <div className="text-foreground-secondary type-ui-xs flex items-center gap-1.5 border-b px-3 py-1.5">
                {speakerEvidence.prefix === 'mic' ? <Mic className="size-3" /> : <Monitor className="size-3" />}
                <span className="truncate">
                  {AUDIO_SOURCE_LABELS[speakerEvidence.prefix === 'mic' ? 'microphone' : 'system']}
                </span>
                {speakerEvidence.totalVotes > 0 && (
                  <>
                    <span className="text-foreground-placeholder">·</span>
                    <span className="tabular-nums">
                      {speakerEvidence.totalVotes} {speakerEvidence.totalVotes === 1 ? 'segment' : 'segments'}{' '}
                      attributed
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="text-foreground-secondary size-3.5 shrink-0" />
              <Combobox.Input
                ref={inputRef}
                onFocus={selectNameOnOpen}
                placeholder="Type a name…"
                spellCheck={false}
                autoComplete="off"
                className="text-foreground placeholder:text-foreground-placeholder h-6 w-full rounded-none border-0 bg-transparent p-0 text-sm outline-none"
              />
            </div>

            <div className="max-h-56 overflow-y-auto overscroll-contain p-1">
              <Combobox.Empty className="text-foreground-secondary px-3 py-3 text-center text-xs">
                No matching participants
              </Combobox.Empty>

              <Combobox.List>
                {(name: string) => (
                  <Combobox.Item key={name} value={name}>
                    {isNewName && name === trimmedQuery ? (
                      <span className="min-w-0 flex-1 truncate">
                        Use <span className="font-medium">&ldquo;{name}&rdquo;</span>
                      </span>
                    ) : (
                      <>
                        <span className="min-w-0 flex-1 truncate">{name}</span>
                        {(speakerEvidence?.votes[name] ?? 0) > 0 && (
                          <span className="text-foreground-secondary shrink-0 text-xs tabular-nums">
                            {speakerEvidence?.votes[name]} {speakerEvidence?.votes[name] === 1 ? 'seg' : 'segs'}
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Item>
                )}
              </Combobox.List>
            </div>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox>
  )
})
